// src/pages/admin/members/AdminLegacyFellows.tsx
//
// Legacy Fellows nominations pipeline.
//
// ACCESS:
//   admin / committee_chair — view all nominations, select, decline, publish reports
//   regional_rep            — nominate mentees; view nominations (read-only actions)
//   portal_manager / committee_member — read-only; no actions
//
// ACTIONS CAPTURED:
//   GET   /fellows/nominations       — full pipeline
//   POST  /fellows/nominate          — rep nominates a mentee
//   PATCH /fellows/:id/select        — confirm + add shadowing dates (10-cap enforced by backend)
//   PATCH /fellows/:id/decline       — decline with optional reason
//   PATCH /fellows/:id/report        — portal_manager / admin publishes post-shadowing report

import React, { useState, useMemo } from "react";
import {
  Star,
  CheckCircle2,
  XCircle,
  Award,
  Loader2,
  Check,
  X,
  Calendar,
  MapPin,
  Eye,
} from "lucide-react";

import {
  CAN_VIEW_FELLOWS,
  CAN_SELECT_FELLOW,
  TIER_META,
  FELLOWS_STATUS_META,
  QUARTERS,
  Bone,
  FL,
  Input,
  Textarea,
  Sel,
  fmtDate,
  ConfirmModal,
  StatTile,
} from "./MemberShared";
import {
  FellowRecord,
  FellowsStatus,
  useDeclineFellowMutation,
  useGetNominationsQuery,
  useSelectFellowMutation,
  useUpdateFellowReportMutation,
} from "../../app/api/fellowsApi";
import { UserRole, MemberTier } from "../../app/api/authApi";
import { useAppSelector } from "../../app/hooks";
import { QuarterKey } from "../../app/api/programmeApi";

/* ─────────────────────────────────────────────────────────────────
   TYPE GUARD (fixes TS7053: safe indexing into Record<MemberTier, ...>)
───────────────────────────────────────────────────────────────── */
const isMemberTier = (v: unknown): v is MemberTier =>
  v === "wider_audience" || v === "inner_circle" || v === "legacy_fellows";

/* ─────────────────────────────────────────────────────────────────
   SELECT FELLOW MODAL
───────────────────────────────────────────────────────────────── */
const SelectFellowModal: React.FC<{
  fellow: FellowRecord;
  isLoading: boolean;
  apiError: string | null;
  onConfirm: (data: {
    shadowingStartDate?: string;
    shadowingEndDate?: string;
    shadowingLocation?: string;
    logisticsNotes?: string;
  }) => void;
  onCancel: () => void;
}> = ({ fellow, isLoading, apiError, onConfirm, onCancel }) => {
  const [startDate, setStart] = useState("");
  const [endDate, setEnd] = useState("");
  const [location, setLocation] = useState("");
  const [logistics, setLogistics] = useState("");
  const mentee = fellow.mentee as any;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
            <Award size={18} className="text-green-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">
              Confirm selection
            </h3>
            <p className="text-xs text-gray-500">
              {mentee?.firstName} {mentee?.lastName} · {fellow.quarter}
            </p>
          </div>
        </div>
        <div className="space-y-3 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FL>Shadowing start</FL>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <FL>Shadowing end</FL>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>
          <div>
            <FL>Location</FL>
            <Input
              placeholder="e.g. Lagos Regional Office"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
          <div>
            <FL>
              Logistics notes{" "}
              <span className="font-normal text-gray-400">(internal)</span>
            </FL>
            <Textarea
              rows={2}
              placeholder="Internal notes for the logistics team…"
              value={logistics}
              onChange={(e) => setLogistics(e.target.value)}
            />
          </div>
        </div>
        {apiError && (
          <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3">
            {apiError}
          </p>
        )}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onConfirm({
                shadowingStartDate: startDate || undefined,
                shadowingEndDate: endDate || undefined,
                shadowingLocation: location || undefined,
                logisticsNotes: logistics || undefined,
              })
            }
            disabled={isLoading}
            className="flex-1 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {isLoading ? "Confirming…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   REPORT PANEL  (publish post-shadowing report)
───────────────────────────────────────────────────────────────── */
const ReportPanel: React.FC<{
  fellow: FellowRecord;
  isLoading: boolean;
  apiError: string | null;
  onSave: (data: {
    title?: string;
    body?: string;
    excerpt?: string;
    keyLesson?: string;
    isPublished?: boolean;
  }) => void;
  onClose: () => void;
}> = ({ fellow, isLoading, apiError, onSave, onClose }) => {
  const existing = fellow.report;
  const [title, setTitle] = useState(existing?.title ?? "");
  const [body, setBody] = useState(existing?.body ?? "");
  const [excerpt, setExcerpt] = useState(existing?.excerpt ?? "");
  const [keyLesson, setKeyLesson] = useState(existing?.keyLesson ?? "");
  const [publish, setPublish] = useState(existing?.isPublished ?? false);
  const mentee = fellow.mentee as any;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden">
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-sm font-bold text-gray-900">
              Post-shadowing report
            </h2>
            <p className="text-xs text-gray-400">
              {mentee?.firstName} {mentee?.lastName} · {fellow.quarter}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
          <div>
            <FL required>Report title</FL>
            <Input
              placeholder="e.g. Legacy Journey — Q2 2025"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <FL required>Report body</FL>
            <Textarea
              rows={7}
              placeholder="Full narrative of the shadowing experience…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={5000}
            />
            <p className="text-right text-[11px] text-gray-400 mt-0.5">
              {body.length}/5000
            </p>
          </div>
          <div>
            <FL>
              Excerpt{" "}
              <span className="font-normal text-gray-400">
                (shown on Fellows tab card)
              </span>
            </FL>
            <Textarea
              rows={2}
              placeholder="One-sentence summary…"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              maxLength={300}
            />
          </div>
          <div>
            <FL>Key lesson</FL>
            <Input
              placeholder="The single most impactful takeaway…"
              value={keyLesson}
              onChange={(e) => setKeyLesson(e.target.value)}
              maxLength={200}
            />
          </div>
          {/* Publish toggle */}
          <label className="flex items-start gap-3 cursor-pointer">
            <div className="relative mt-0.5 shrink-0">
              <input
                type="checkbox"
                className="sr-only"
                checked={publish}
                onChange={(e) => setPublish(e.target.checked)}
              />
              <div
                className={`w-9 h-5 rounded-full transition-colors ${
                  publish ? "bg-purple-600" : "bg-gray-200"
                }`}
              />
              <div
                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  publish ? "translate-x-4" : ""
                }`}
              />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                Publish report
              </p>
              <p className="text-[11px] text-gray-400">
                Visible to all members on the Fellows tab.
              </p>
            </div>
          </label>
          {apiError && (
            <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {apiError}
            </p>
          )}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() =>
                onSave({
                  title: title || undefined,
                  body: body || undefined,
                  excerpt: excerpt || undefined,
                  keyLesson: keyLesson || undefined,
                  isPublished: publish,
                })
              }
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:scale-95 disabled:opacity-50 shadow-sm"
            >
              {isLoading ? (
                <>
                  <Loader2 size={13} className="animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Check size={13} />
                  {publish ? "Save & publish" : "Save draft"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   FELLOW CARD
───────────────────────────────────────────────────────────────── */
const FellowCard: React.FC<{
  fellow: FellowRecord;
  canSelect: boolean;
  canReport: boolean;
  onSelect: (f: FellowRecord) => void;
  onDecline: (f: FellowRecord) => void;
  onReport: (f: FellowRecord) => void;
}> = ({ fellow, canSelect, canReport, onSelect, onDecline, onReport }) => {
  const sm = FELLOWS_STATUS_META[fellow.status];
  const mentee = fellow.mentee as any;

  // ✅ Fix TS7053 by safely narrowing mentee.tier before indexing TIER_META
  const tierKey: unknown = mentee?.tier;
  const tierMeta = isMemberTier(tierKey) ? TIER_META[tierKey] : null;

  const borderColour =
    fellow.status === "selected"
      ? "border-green-200"
      : fellow.status === "declined"
      ? "border-red-100"
      : fellow.status === "completed"
      ? "border-purple-200"
      : "border-gray-100";

  const topBar =
    fellow.status === "selected"
      ? "bg-green-400"
      : fellow.status === "declined"
      ? "bg-red-300"
      : fellow.status === "completed"
      ? "bg-purple-400"
      : "bg-amber-400";

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden hover:shadow-md transition-all
      ${borderColour} ${fellow.status === "declined" ? "opacity-70" : ""}`}
    >
      <div className={`h-1 w-full ${topBar}`} />
      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700 shrink-0">
              {mentee?.firstName?.[0]}
              {mentee?.lastName?.[0]}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {mentee?.firstName} {mentee?.lastName}
              </p>
              <p className="text-[11px] text-gray-400 truncate">
                {mentee?.email ?? mentee?.region ?? "—"}
              </p>
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${sm.colour}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`} />
            {sm.label}
          </span>
        </div>

        {/* Quarter + tier */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
            {fellow.quarter}
          </span>

          {tierMeta ? (
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tierMeta.colour}`}
            >
              {tierMeta.label}
            </span>
          ) : mentee?.tier ? (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
              {String(mentee.tier)}
            </span>
          ) : null}

          {mentee?.ministryRole && (
            <span className="text-[10px] text-gray-400 truncate">
              {mentee.ministryRole}
            </span>
          )}
        </div>

        {/* Nomination comment */}
        {fellow.nominationComment && (
          <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-2 italic">
            "{fellow.nominationComment}"
          </p>
        )}

        {/* Nominated by */}
        {(fellow.nominatedBy as any)?.firstName && (
          <p className="text-[11px] text-gray-400">
            By {(fellow.nominatedBy as any).firstName}{" "}
            {(fellow.nominatedBy as any).lastName} · {fmtDate(fellow.nominatedAt)}
          </p>
        )}

        {/* Shadowing block */}
        {fellow.status === "selected" && fellow.shadowingStartDate && (
          <div className="bg-green-50 rounded-lg px-3 py-2 text-[11px] text-green-700 space-y-0.5">
            <p className="font-semibold flex items-center gap-1">
              <Calendar size={10} /> Shadowing week
            </p>
            <p>
              {fmtDate(fellow.shadowingStartDate)} →{" "}
              {fmtDate(fellow.shadowingEndDate)}
            </p>
            {fellow.shadowingLocation && (
              <p className="flex items-center gap-1">
                <MapPin size={9} />
                {fellow.shadowingLocation}
              </p>
            )}
          </div>
        )}

        {/* Report badge */}
        {fellow.report?.isPublished && (
          <div className="flex items-center gap-1.5 text-[11px] text-purple-600">
            <Eye size={10} /> Report published
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-gray-50 flex-wrap">
          {canSelect &&
            (fellow.status === "nominated" || fellow.status === "shortlisted") && (
              <>
                <button
                  onClick={() => onSelect(fellow)}
                  className="flex-1 text-xs font-semibold py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:scale-95 transition-all"
                >
                  <span className="flex items-center justify-center gap-1">
                    <CheckCircle2 size={11} /> Select
                  </span>
                </button>
                <button
                  onClick={() => onDecline(fellow)}
                  className="flex-1 text-xs font-semibold py-1.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <span className="flex items-center justify-center gap-1">
                    <XCircle size={11} /> Decline
                  </span>
                </button>
              </>
            )}
          {canReport && fellow.status === "selected" && (
            <button
              onClick={() => onReport(fellow)}
              className="flex-1 text-xs font-semibold py-1.5 border border-purple-200 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors"
            >
              <span className="flex items-center justify-center gap-1">
                <Award size={11} /> {fellow.report ? "Edit report" : "Write report"}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   PAGE
───────────────────────────────────────────────────────────────── */
const AdminLegacyFellows: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;
  const role = (user.role ?? "committee_member") as UserRole;

  const canView = CAN_VIEW_FELLOWS.includes(role);
  const canSelect = CAN_SELECT_FELLOW.includes(role);
  // portal_manager and admin can publish reports
  const canReport = ["admin", "portal_manager", "committee_chair"].includes(role);

  const [fellowsQ, setFellowsQ] = useState<QuarterKey | "all">("all");
  const [statusFilter, setStatusFilter] = useState<FellowsStatus | "all">("all");
  const [selectTarget, setSelectTarget] = useState<FellowRecord | null>(null);
  const [declineTarget, setDeclineTarget] = useState<FellowRecord | null>(null);
  const [declineReason, setDeclineReason] = useState("");
  const [reportTarget, setReportTarget] = useState<FellowRecord | null>(null);
  const [selectErr, setSelectErr] = useState<string | null>(null);
  const [reportErr, setReportErr] = useState<string | null>(null);

  const { data: fellowsRes, isLoading } = useGetNominationsQuery(
    {
      ...(fellowsQ !== "all" ? { quarter: fellowsQ as QuarterKey } : {}),
      ...(statusFilter !== "all" ? { status: statusFilter as FellowsStatus } : {}),
    },
    { skip: !canView }
  );

  const [selectFellow, { isLoading: isSelecting }] = useSelectFellowMutation();
  const [declineFellow, { isLoading: isDeclining }] = useDeclineFellowMutation();
  const [updateFellowReport, { isLoading: isSavingReport }] =
    useUpdateFellowReportMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fellows = fellowsRes?.data ?? [];

  const stats = useMemo(
    () => ({
      total: fellows.length,
      nominated: fellows.filter((f) => f.status === "nominated").length,
      selected: fellows.filter((f) => f.status === "selected").length,
      declined: fellows.filter((f) => f.status === "declined").length,
    }),
    [fellows]
  );

  const handleSelect = async (data: {
    shadowingStartDate?: string;
    shadowingEndDate?: string;
    shadowingLocation?: string;
    logisticsNotes?: string;
  }) => {
    if (!selectTarget) return;
    setSelectErr(null);
    try {
      await selectFellow({ id: selectTarget.id, ...data }).unwrap();
      setSelectTarget(null);
    } catch (err: any) {
      setSelectErr(err?.data?.error ?? "Could not select fellow.");
    }
  };

  const handleDecline = async () => {
    if (!declineTarget) return;
    try {
      await declineFellow({
        id: declineTarget.id,
        declineReason: declineReason || undefined,
      }).unwrap();
      setDeclineTarget(null);
      setDeclineReason("");
    } catch {
      /* swallow */
    }
  };

  const handleSaveReport = async (data: {
    title?: string;
    body?: string;
    excerpt?: string;
    keyLesson?: string;
    isPublished?: boolean;
  }) => {
    if (!reportTarget) return;
    setReportErr(null);
    try {
      await updateFellowReport({ id: reportTarget.id, ...data }).unwrap();
      setReportTarget(null);
    } catch (err: any) {
      setReportErr(err?.data?.error ?? "Could not save report.");
    }
  };

  /* Access gate */
  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
        <Star size={28} className="text-gray-300" />
        <p className="text-sm font-semibold text-gray-600">Access restricted</p>
        <p className="text-xs text-gray-400 max-w-xs">
          Legacy Fellows management requires Admin, Committee Chair, or Regional
          Rep access.
        </p>
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-5 pb-8">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile
          label="Total nominated"
          value={stats.total}
          colour="text-purple-700"
          icon={<Star size={12} className="text-purple-400" />}
          loading={isLoading}
        />
        <StatTile
          label="Pending review"
          value={stats.nominated}
          colour="text-amber-600"
          icon={<span className="w-2 h-2 rounded-full bg-amber-400" />}
        />
        <StatTile
          label="Selected"
          value={stats.selected}
          colour="text-green-700"
          icon={<CheckCircle2 size={12} className="text-green-400" />}
        />
        <StatTile
          label="Declined"
          value={stats.declined}
          colour="text-red-500"
          icon={<XCircle size={12} className="text-red-400" />}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
          {(["all", ...QUARTERS] as const).map((q) => (
            <button
              key={q}
              onClick={() => setFellowsQ(q as QuarterKey | "all")}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all
                ${
                  fellowsQ === q
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
            >
              {q === "all" ? "All" : q}
            </button>
          ))}
        </div>
        <Sel
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="w-36"
        >
          <option value="all">All statuses</option>
          {Object.entries(FELLOWS_STATUS_META).map(([s, m]) => (
            <option key={s} value={s}>
              {m.label}
            </option>
          ))}
        </Sel>
      </div>

      {/* Fellows grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Bone key={i} className="h-56" />
          ))}
        </div>
      ) : fellows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
          <Star size={24} className="text-gray-300" />
          <p className="text-sm font-semibold text-gray-600">
            No nominations found
          </p>
          <p className="text-xs text-gray-400 max-w-xs">
            {role === "regional_rep"
              ? "Go to All Members and use the ⭐ button on a mentee row to nominate them."
              : "Nominations appear here once Regional Reps submit them."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {fellows.map((f) => (
            <FellowCard
              key={f.id}
              fellow={f}
              canSelect={canSelect}
              canReport={canReport}
              onSelect={setSelectTarget}
              onDecline={setDeclineTarget}
              onReport={setReportTarget}
            />
          ))}
        </div>
      )}

      {/* ── Modals ── */}
      {selectTarget && (
        <SelectFellowModal
          fellow={selectTarget}
          isLoading={isSelecting}
          apiError={selectErr}
          onConfirm={handleSelect}
          onCancel={() => {
            setSelectTarget(null);
            setSelectErr(null);
          }}
        />
      )}

      {declineTarget && (
        <ConfirmModal
          title={`Decline ${(declineTarget.mentee as any)?.firstName}'s nomination?`}
          message="This clears their fellows-eligible flag. They can be re-nominated in a future quarter."
          confirmLabel="Decline nomination"
          danger
          isLoading={isDeclining}
          onConfirm={handleDecline}
          onCancel={() => {
            setDeclineTarget(null);
            setDeclineReason("");
          }}
        >
          <div className="mb-1">
            <FL>
              Reason{" "}
              <span className="font-normal text-gray-400">
                (optional, internal)
              </span>
            </FL>
            <Input
              placeholder="Internal reason…"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
            />
          </div>
        </ConfirmModal>
      )}

      {reportTarget && (
        <ReportPanel
          fellow={reportTarget}
          isLoading={isSavingReport}
          apiError={reportErr}
          onSave={handleSaveReport}
          onClose={() => {
            setReportTarget(null);
            setReportErr(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminLegacyFellows;